import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { LogoMark } from "@/components/Shell";
import { Reveal } from "@/components/Primitives";
import { useAuth } from "@/context/AuthContext";

const HERO = "/images/landing/luxury-ai-corporate-lobby.png";
const STEPS = [
  { n: "01", title: "Tell us who you are", body: "A warm conversation — type or speak — becomes your Business DNA. Nothing is invented; every field shows where it came from.", img: "/images/landing/ai-powered-business-strategy-workspace.png", alt: "Entrepreneur working with an AI business strategy assistant" },
  { n: "02", title: "See your brand", body: "Wordmark, palette with roles, a Google Fonts pairing, a six-image mood board, mission and voice — unveiled on one page, saved as Brand Tokens.", img: "/images/landing/strategy-creates-opportunity.png", alt: "Brand strategy proposal and market insights" },
  { n: "03", title: "Launch your kit", body: "One button: a four-page website with generated imagery, nine designed posts and three ad creatives — each with copy you can lift in one click.", img: "/images/landing/penthouse-executive-sunset-skyline.png", alt: "Executive overlooking a waterfront skyline at sunset" },
];

export default function Landing() {
  const heroRef = useRef(null);
  const reduceMotion = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const { isAuthenticated, org } = useAuth();
  const dest = isAuthenticated ? (org ? "/home" : "/onboarding") : "/auth";
  return (
    <div className="min-h-screen bg-black text-white on-dark">
      <header className="fixed top-0 inset-x-0 z-40 h-16 flex items-center justify-between px-5 sm:px-8">
        <Link to="/" className="flex items-center gap-3" data-testid="landing-logo">
          <LogoMark />
          <span className="font-display text-lg">LegacyStack <span className="text-white/60">AI OS</span></span>
        </Link>
        <div className="flex items-center gap-2">
          <Link to={dest} className="btn-outline border-white/30 text-white hover:border-white/70 h-10" data-testid="landing-signin">{isAuthenticated ? "Open studio" : "Sign in"}</Link>
        </div>
      </header>

      <section ref={heroRef} className="relative min-h-[100svh] flex items-end grain overflow-hidden">
        <motion.img style={{ y: reduceMotion ? 0 : heroY, scale: reduceMotion ? 1 : 1.05 }} initial={false} src={HERO} alt="LegacyStack AI gold logo in a marble corporate lobby overlooking the skyline" className="absolute inset-0 h-full w-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/55 to-black/20" />
        <div className="relative max-w-7xl mx-auto w-full px-5 sm:px-8 pb-20 pt-40">
          <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.9 }} className="eyebrow-label !text-gold mb-6">The luxury AI operating system for builders</motion.p>
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45, duration: 1.1, ease: [0.16, 1, 0.3, 1] }} className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl leading-[1.04] max-w-4xl">
            Tell your story once.<br /><span className="italic font-normal text-white/80">Watch it become a brand.</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 1 }} className="mt-8 max-w-xl text-base md:text-lg text-white/70 leading-relaxed">
            One conversation becomes your Business DNA. Your DNA becomes a brand. Your brand becomes a published website, nine designed posts and three ads — finished, on-brand, copy-ready.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 1 }} className="mt-10 flex flex-wrap items-center gap-4">
            <Link to={dest} className="btn-gold h-13 px-8 text-base" data-testid="begin-cta-button">Begin<ArrowRight className="h-4 w-4" /></Link>
            <span className="text-sm text-white/50">100 trial credits · the first win is free to experience</span>
          </motion.div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-28 sm:py-36">
        <Reveal><p className="eyebrow-label !text-gold mb-4">One guided path</p><h2 className="font-display text-3xl sm:text-4xl lg:text-5xl max-w-3xl leading-[1.08]">Three movements, under twenty minutes, one finished brand.</h2></Reveal>
        <div className="mt-20 space-y-24">
          {STEPS.map((s, i) => (
            <Reveal key={s.n} className={`grid md:grid-cols-2 gap-10 md:gap-16 items-center ${i % 2 ? "md:[&>*:first-child]:order-2" : ""}`}>
              <div className="relative aspect-[4/3] overflow-hidden rounded-3xl">
                <img src={s.img} alt={s.alt} className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
              </div>
              <div>
                <span className="font-mono-ls text-xs text-gold">{s.n}</span>
                <h3 className="font-display text-2xl sm:text-3xl mt-3">{s.title}</h3>
                <p className="mt-4 text-white/65 leading-relaxed">{s.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 grid md:grid-cols-3 gap-10">
          {[["Nothing invented", "Empty fields stay empty. Provenance on every DNA field. Claims discipline on every word."], ["Finished, not drafted", "Every output is the real thing — rendered posts, a live site, designed covers — with copy beside it."], ["For business and ministry", "Choose your path and every label, question and output speaks your language."]].map(([t, b]) => (
            <Reveal key={t}><div className="gold-rule mb-5" /><h3 className="font-display text-xl">{t}</h3><p className="mt-3 text-sm text-white/60 leading-relaxed">{b}</p></Reveal>
          ))}
        </div>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pb-24 flex flex-col items-start gap-6">
          <Link to={dest} className="btn-gold h-12 px-8" data-testid="landing-bottom-cta">Begin your first win<ArrowRight className="h-4 w-4" /></Link>
          <p className="text-xs text-white/40 flex items-center gap-2"><LogoMark className="h-4 w-4" />LegacyStack AI · Wealth, freedom, legacy.</p>
        </div>
      </section>
    </div>
  );
}
